import { useState } from 'react';
import { useDispatch } from 'react-redux';

import dialogsManagerActions from '../../../../../../redux/actions/dialogsManager.actions';
import EventActions from '../../../../../../redux/actions/event-actions/event.actions';
import partnerActions from '../../../../../../redux/actions/partners-actions/partners.actions';
import storesSchedulesActions from '../../../../../../redux/actions/storesSchedules.actions';

import { useMaxCounts } from '../../hooks/useMaxCounts';
import { useChangeDemandStateTexts } from './useChangeDemandStateTexts';

import type { PartnerId } from 'shared/domain';
import type { TTLOption } from 'features/high-demand/shared/models';
import type { Saturation, StoreStateByHDB } from 'business/highDemand';

const DEMAND_TTL_OPTIONS: TTLOption[] = [20, 40, 60, 'day'];
const DEMAND_STATUS_OPTIONS: Saturation[] = ['fast', 'low', 'regular', 'high'];

export function useChangeDemandState(props: Props) {
  const { store, onSelect, partnerId, onClose } = props;

  const storeId = store.id;
  const currentDemand = store.saturated;
  const isEligibleForFastOperation = store.isEligibleForFastOperation;
  const demandStatusOptions = DEMAND_STATUS_OPTIONS.filter(
    d =>
      (currentDemand === 'fast'
        ? d !== 'low'
        : isEligibleForFastOperation
        ? d !== 'low' && d !== currentDemand
        : d !== currentDemand) &&
      !(currentDemand === 'superHigh' && d === 'high'),
  );

  const showFastFromOCC = store.saturated === 'fastFromOCC';
  const showDSOptions = !store.isTurbo && !showFastFromOCC;

  const dispatch = useDispatch();
  const [isFastOperationChecked, setIsFastOperationChecked] = useState(false);
  const [showTTLs, setShowTTLs] = useState(false);
  const [ttlSelected, setTTLSelected] = useState<TTLOption>(0);
  const [demandSelected, setDemandSelected] = useState<Saturation>(
    'deactivated',
  );

  const counts = useMaxCounts();
  const maxUses = counts.getFlagsForMaxCountsByStore({ storeId });
  const showHasMaxUse =
    (maxUses.hasMaxPerWeek || maxUses.hasMaxPerDay) && showDSOptions;

  const texts = useChangeDemandStateTexts({
    hasMaxUsesPerDay: maxUses.hasMaxPerDay,
    hasMaxUsesPerWeek: maxUses.hasMaxPerWeek,
  });

  const onSelectDemand = (demand: Saturation) => {
    setShowTTLs(
      demand === 'high' ||
        demand === 'low' ||
        demand === 'superHigh' ||
        (demand === 'regular' && isEligibleForFastOperation),
    );
    setDemandSelected(demand);
    setTTLSelected(0);
    onSelect({ ttl: 0, severity: demand });
  };

  const onSelectTTL = (ttl: TTLOption) => {
    setTTLSelected(ttl);
    onSelect({
      severity: demandSelected,
      ttl: (ttl === 'day' ? remainingMinutesForTheNextDay() : ttl) as number,
    });
  };

  const onOpenExtendClosingHoursDialog = async () => {
    dispatch(dialogsManagerActions.openExtendClosingHoursDialog(storeId));
    dispatch(storesSchedulesActions.applyStoresSchedulesFilter(storeId));
    EventActions.onExtendedSchedules({ storeId, partnerId });
    onClose();
  };

  const onDeactivateStore = async () => {
    const payload = { storeIds: [storeId], enabled: false };
    const deactivatedAction = partnerActions.switchPartnerStoresStatus(payload);
    dispatch(deactivatedAction);
    onClose();
  };

  return {
    texts,
    showHasMaxUse,
    showFastFromOCC,
    showDSOptions,
    showTTLs,
    ttlSelected,
    isFastOperationChecked,
    demandSelected,
    demandStatusOptions,
    ttlOptions: DEMAND_TTL_OPTIONS,
    onSelectDemand,
    onSelectTTL,
    onOpenExtendClosingHoursDialog,
    onDeactivateStore,
    setIsFastOperationChecked,
  };
}

interface Props {
  store: StoreStateByHDB;
  onSelect: (params: { ttl: number; severity: Saturation }) => void;
  partnerId: PartnerId;
  onClose: () => void;
}

function remainingMinutesForTheNextDay() {
  const [MS_IN_SEG, SEG_IN_MINUTE] = [1_000, 60];
  const now = new Date();
  const midnight = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 1,
  ).getTime();
  const remainingTime: number = midnight - now.getTime();
  return Math.floor(remainingTime / (MS_IN_SEG * SEG_IN_MINUTE));
}
